'use client';

import {
  Indicator,
  type CheckboxProps as RadixCheckboxProps,
  Root,
} from '@radix-ui/react-checkbox';
import clsx from 'clsx';
import { useField } from 'formik';
import type { ReactNode } from 'react';

import Flex from '@commons/containers/Flex';

import styles from './index.module.scss';

/**
 * Checkbox component props.
 */

export type CheckboxProps = RadixCheckboxProps & {
  name: string;
  error?: boolean;
  children?: ReactNode;
};

/**
 * Uncontrolled Checkbox component (based on Radix checkbox primitive).
 *
 * This export can be used as an uncontrolled component or with a custom controller.
 */

export const Checkbox = ({ name, id, error, children, className, ...props }: CheckboxProps) => {
  const classes = clsx(styles.fieldset, error && styles.error, className);
  const fieldId = id || name;

  return (
    <Flex as="fieldset" gap="xxs" align="center" className={classes}>
      <Root id={fieldId} name={name} className={styles.checkbox} {...props}>
        <Indicator />
      </Root>
      {children && <label htmlFor={fieldId}>{children}</label>}
    </Flex>
  );
};

/**
 * Formik-controlled Checkbox component.
 *
 * This export should be used inside a Formik form.
 */

export const FormikCheckbox = ({ name, children, ...props }: CheckboxProps) => {
  const [field, meta, helpers] = useField(name);

  const { value, touched, error } = meta;
  const { setValue } = helpers;

  const isError = touched && error !== undefined;

  return (
    <Checkbox checked={value} onCheckedChange={setValue} error={isError} {...field} {...props}>
      {children}
    </Checkbox>
  );
};

export default Checkbox;
